import axios from '@Utils/httpService';
import { error } from '@/utils/debugLogger';

/**
 * dashboard-configuration.service.js
 *
 * Per-TAB (DashboardPage) dashboard configuration — currently the coloring-schema registry
 * (COLORING_SCHEMA_REGISTRY.md) under the `coloringSchemas` key, alongside `layout.contentWidth`.
 * The configuration is content-split from the 5-field tab PUT so a layout/name save can never wipe
 * it, mirroring how DashboardWidget.SqlViewConfig rides alongside the widget row. Keyed by TAB
 * (DashboardPage) id, not facility.
 *
 * WARNING: the route + response DTO are PINNED in the plan but UNCONFIRMED until the aiva.api
 * builder finishes Step 1 (entity + controller + migration). The consumers' parsers are tolerant of
 * the envelope; adjust `baseUrl` here if the final api report deviates from this route.
 */
const baseUrl = 'api/CustomDashboards';

// ── Envelope + tolerant parse (mirrors CustomDashboard's extractRawConfiguration/toConfigObject) ──
// Unwrap the raw `configuration` value (string | object | null) from a GET response envelope,
// tolerant of the (Step-1-UNCONFIRMED) shape: httpService may hand back `{ id, configuration }`
// directly or a `{ data: {...} }` wrapper.
function extractRawConfiguration(response) {
  const body = response && response.data;
  if (!body) return null;
  if (body.configuration !== undefined) return body.configuration;
  if (body.data && body.data.configuration !== undefined) return body.data.configuration;
  return null;
}

// Coerce a raw `configuration` (JSON string | object | null) into a plain config object for
// read-modify-write. null / absent / unparseable → {} (never throws), matching the manager and
// CustomDashboard so every writer sees the same tolerant shape.
function toConfigObject(configuration) {
  if (configuration == null || configuration === '') return {};
  try {
    return typeof configuration === 'string' ? (JSON.parse(configuration) || {}) : configuration;
  } catch (err) {
    error('[dashboard-configuration] configuration unparseable', err);
    return {};
  }
}

export default {
  /**
   * GET a tab's configuration → `{ id, configuration }`, where `configuration` is the raw JSON
   * string (or null for tabs saved before this feature).
   * @param {string} tabId DashboardPage id
   */
  getConfiguration(tabId) {
    return axios.get(`${baseUrl}/${tabId}/configuration`);
  },
  /**
   * PUT a tab's configuration (raw pass-through); body `{ configuration }`. Used by the manager
   * dialog after schema create/edit/delete.
   * @param {string} tabId DashboardPage id
   * @param {string} configuration raw JSON string, e.g. `{ formatVersion, coloringSchemas }`
   */
  putConfiguration(tabId, configuration) {
    return axios.put({ url: `${baseUrl}/${tabId}/configuration`, data: { configuration } });
  },
  /**
   * Read-modify-write a tab's configuration with a FRESH GET as the base, so a concurrent
   * layout/width save (or any unknown future key) is preserved rather than clobbered. Shallow-merges
   * `mergePatch` over the freshly-fetched config — the patch fully REPLACES any key it names (it does
   * not deep-merge, so it cannot reconcile a concurrent edit to that same key) — then PUTs and
   * returns the saved config object.
   *
   * THROWS if the GET or the PUT fails, and never PUTs after a failed GET: the caller must fail
   * closed (surface an error, write nothing) rather than persist a partial blob built on a base it
   * could not read.
   * @param {string} tabId DashboardPage id
   * @param {object} mergePatch keys to overlay on the fresh config, e.g. `{ coloringSchemas }`
   * @returns {Promise<object>} the merged config object that was PUT
   */
  async updateTabConfiguration(tabId, mergePatch) {
    const res = await this.getConfiguration(tabId);
    const freshConfig = toConfigObject(extractRawConfiguration(res));
    const nextConfig = { ...freshConfig, ...mergePatch };
    await this.putConfiguration(tabId, JSON.stringify(nextConfig));
    return nextConfig;
  },
};
